import React, { useState, useEffect } from 'react';
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  IconButton,
  Grid,
  Box,
  Typography
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { FormSection, FormActions, Card } from './ui';
import { useFormValidation } from '../hooks/useFormValidation';
import PhotoUploadSection from './PhotoUploadSection';
import api from '../utils/api';

const FACILITY_TYPES = [
  { value: 'gym', label: 'Gym' },
  { value: 'pool', label: 'Pool' },
  { value: 'court', label: 'Court' },
  { value: 'studio', label: 'Studio' },
  { value: 'track', label: 'Track' },
  { value: 'field', label: 'Field' },
  { value: 'room', label: 'Room' }
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'inactive', label: 'Inactive' }
];

const DAYS_OF_WEEK = [
  'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
];

const FacilityForm = ({ facility, onSubmit, onCancel }) => {
  const initialData = {
    name: '',
    description: '',
    type: 'gym',
    capacity: '',
    status: 'active',
    operatingHours: [],
    bookingConfig: {
      slotDuration: 60,
      leadTime: 1,
      maxBookingDuration: 120
    },
    isActive: true
  };

  const [photos, setPhotos] = useState([]);
  const [coverPhotoId, setCoverPhotoId] = useState(null);

  const validationRules = {
    name: [
      (value) => !value?.trim() ? 'Facility name is required' : null,
      (value) => value?.length > 100 ? 'Facility name cannot exceed 100 characters' : null
    ],
    description: [
      (value) => !value?.trim() ? 'Facility description is required' : null,
      (value) => value?.length > 500 ? 'Facility description cannot exceed 500 characters' : null
    ],
    capacity: [
      (value) => {
        const capacity = parseInt(value);
        if (!value || isNaN(capacity)) return 'Valid capacity is required';
        if (capacity < 1) return 'Capacity must be at least 1';
        if (capacity > 500) return 'Capacity cannot exceed 500';
        return null;
      }
    ]
  };

  const {
    data: formData,
    isSubmitting,
    updateField,
    updateFields,
    handleSubmit,
    setFormData,
    getFieldError,
    hasFieldError
  } = useFormValidation(initialData, validationRules);

  const [operatingHoursErrors, setOperatingHoursErrors] = useState({});
  const [bookingConfigErrors, setBookingConfigErrors] = useState({});

  useEffect(() => {
    if (facility) {
      setFormData({
        name: facility.name || '',
        description: facility.description || '',
        type: facility.type || 'gym',
        capacity: facility.capacity?.toString() || '',
        status: facility.status || 'active',
        operatingHours: facility.operatingHours || [],
        bookingConfig: {
          slotDuration: facility.bookingConfig?.slotDuration || 60,
          leadTime: facility.bookingConfig?.leadTime || 1,
          maxBookingDuration: facility.bookingConfig?.maxBookingDuration || 120
        },
        isActive: facility.isActive !== undefined ? facility.isActive : true
      });

      // Load facility photos if editing existing facility
      if (facility._id) {
        loadFacilityPhotos(facility._id);
      }
    }
  }, [facility, setFormData]);

  const loadFacilityPhotos = async (facilityId) => {
    try {
      const response = await api.get(`/facilities/${facilityId}/photos`);
      if (response.data.success) {
        setPhotos(response.data.data.photos || []);
        setCoverPhotoId(response.data.data.coverPhoto?._id || null);
      }
    } catch (error) {
      console.error('Error loading facility photos:', error);
    }
  };

  const handlePhotoUpload = async (formData) => {
    if (!facility?._id) {
      alert('Please save the facility first before uploading photos');
      return;
    }

    try {
      const response = await api.post(`/facilities/${facility._id}/photos`, formData);

      if (response.data.success) {
        // Reload photos to get updated list
        await loadFacilityPhotos(facility._id);
      }
    } catch (error) {
      console.error('Error uploading photos:', error);
      throw error;
    }
  };

  const handlePhotoDelete = async (photoId) => {
    if (!facility?._id) return;

    try {
      await api.delete(`/facilities/${facility._id}/photos/${photoId}`);
      // Reload photos to get updated list
      await loadFacilityPhotos(facility._id);
    } catch (error) {
      console.error('Error deleting photo:', error);
    }
  };

  const handleSetCoverPhoto = async (photoId) => {
    if (!facility?._id) return;

    try {
      await api.put(`/facilities/${facility._id}/cover-photo`, {
        photoId
      });
      setCoverPhotoId(photoId);
    } catch (error) {
      console.error('Error setting cover photo:', error);
    }
  };

  // Handle nested field updates for booking config
  const handleNestedFieldChange = (parent, field, value) => {
    updateFields({
      [parent]: {
        ...formData[parent],
        [field]: value
      }
    });
    
    // Clear booking config errors for this field
    if (bookingConfigErrors[field]) {
      setBookingConfigErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const addOperatingHours = () => {
    const availableDays = DAYS_OF_WEEK.filter(day => 
      !formData.operatingHours.some(hours => hours.day === day)
    );
    
    if (availableDays.length > 0) {
      updateFields({
        operatingHours: [
          ...formData.operatingHours,
          {
            day: availableDays[0],
            openTime: '09:00',
            closeTime: '17:00',
            isClosed: false
          }
        ]
      });
    }
  };

  const removeOperatingHours = (index) => {
    updateFields({
      operatingHours: formData.operatingHours.filter((_, i) => i !== index)
    });
    
    // Clear errors for removed operating hours
    if (operatingHoursErrors[index]) {
      const newErrors = { ...operatingHoursErrors };
      delete newErrors[index];
      setOperatingHoursErrors(newErrors);
    }
  };

  const updateOperatingHours = (index, field, value) => {
    const updatedHours = formData.operatingHours.map((hours, i) => 
      i === index ? { ...hours, [field]: value } : hours
    );
    
    updateFields({ operatingHours: updatedHours });
    
    // Clear operating hours errors for this field
    if (operatingHoursErrors[index]?.[field]) {
      setOperatingHoursErrors(prev => ({
        ...prev,
        [index]: {
          ...prev[index],
          [field]: null
        }
      }));
    }
  };

  const validateOperatingHours = () => {
    const errors = {};
    const usedDays = new Set();
    
    formData.operatingHours.forEach((hours, index) => {
      const hourErrors = {};
      
      if (usedDays.has(hours.day)) {
        hourErrors.day = 'Duplicate day';
      } else {
        usedDays.add(hours.day);
      }
      
      if (!hours.isClosed) {
        if (!hours.openTime) {
          hourErrors.openTime = 'Open time is required';
        } else if (!/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(hours.openTime)) {
          hourErrors.openTime = 'Invalid time format (HH:MM)';
        }
        
        if (!hours.closeTime) {
          hourErrors.closeTime = 'Close time is required';
        } else if (!/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(hours.closeTime)) {
          hourErrors.closeTime = 'Invalid time format (HH:MM)';
        }
        
        if (hours.openTime && hours.closeTime && 
            /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(hours.openTime) &&
            /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(hours.closeTime)) {
          const [openHour, openMin] = hours.openTime.split(':').map(Number);
          const [closeHour, closeMin] = hours.closeTime.split(':').map(Number);
          const openMinutes = openHour * 60 + openMin;
          const closeMinutes = closeHour * 60 + closeMin;
          
          if (closeMinutes <= openMinutes) {
            hourErrors.closeTime = 'Close time must be after open time';
          }
        }
      }
      
      if (Object.keys(hourErrors).length > 0) {
        errors[index] = hourErrors;
      }
    });
    
    setOperatingHoursErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateBookingConfig = () => {
    const errors = {};
    
    if (formData.bookingConfig.slotDuration < 15 || formData.bookingConfig.slotDuration > 480) {
      errors.slotDuration = 'Slot duration must be between 15 and 480 minutes';
    }
    
    if (formData.bookingConfig.leadTime < 0 || formData.bookingConfig.leadTime > 168) {
      errors.leadTime = 'Lead time must be between 0 and 168 hours';
    }
    
    if (formData.bookingConfig.maxBookingDuration < 15 || formData.bookingConfig.maxBookingDuration > 480) {
      errors.maxBookingDuration = 'Max booking duration must be between 15 and 480 minutes';
    }
    
    if (formData.bookingConfig.slotDuration > formData.bookingConfig.maxBookingDuration) {
      errors.slotDuration = 'Slot duration cannot be greater than max booking duration';
    }
    
    setBookingConfigErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const onFormSubmit = async (data) => {
    // Validate operating hours and booking config
    const operatingHoursValid = validateOperatingHours();
    const bookingConfigValid = validateBookingConfig();
    
    if (!operatingHoursValid || !bookingConfigValid) {
      return;
    }

    const submitData = {
      ...data,
      capacity: parseInt(data.capacity),
      bookingConfig: {
        slotDuration: parseInt(data.bookingConfig.slotDuration),
        leadTime: parseInt(data.bookingConfig.leadTime),
        maxBookingDuration: parseInt(data.bookingConfig.maxBookingDuration)
      }
    };
    
    await onSubmit(submitData);
  };

  const availableDays = DAYS_OF_WEEK.filter(day => 
    !formData.operatingHours.some(hours => hours.day === day)
  );

  return (
    <Box component="form" onSubmit={handleSubmit(onFormSubmit)} sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
      {/* Basic Information Section */}
      <FormSection 
        title="Basic Information" 
        description="Enter the basic details for the facility"
        variant="card"
      >
        <Grid container spacing={3} sx={{ width: '100%' }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Facility Name"
              required
              value={formData.name}
              onChange={(e) => updateField('name', e.target.value)}
              placeholder="Enter facility name"
              error={hasFieldError('name')}
              helperText={getFieldError('name')}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth required>
              <InputLabel>Facility Type</InputLabel>
              <Select
                value={formData.type}
                onChange={(e) => updateField('type', e.target.value)}
                label="Facility Type"
              >
                {FACILITY_TYPES.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              required
              value={formData.description}
              onChange={(e) => updateField('description', e.target.value)}
              placeholder="Enter facility description"
              error={hasFieldError('description')}
              helperText={getFieldError('description')}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              type="number"
              label="Capacity"
              required
              value={formData.capacity}
              onChange={(e) => updateField('capacity', e.target.value)}
              placeholder="Enter capacity"
              error={hasFieldError('capacity')}
              helperText={getFieldError('capacity')}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => updateField('status', e.target.value)}
                label="Status"
              >
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isActive}
                  onChange={(e) => updateField('isActive', e.target.checked)}
                />
              }
              label="Facility is active and available for booking"
              sx={{ mt: 1 }}
            />
          </Grid>
        </Grid>
      </FormSection>

      {/* Operating Hours Section */}
      <FormSection 
        title="Operating Hours" 
        description="Configure when the facility is available"
        variant="card"
        collapsible
        defaultExpanded={true}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {formData.operatingHours.map((hours, index) => (
            <Card key={index} sx={{ p: 2 }}>
              <Grid container spacing={2} alignItems="center" sx={{ width: '100%' }}>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <FormControl fullWidth error={!!operatingHoursErrors[index]?.day}>
                    <InputLabel>Day</InputLabel>
                    <Select
                      value={hours.day}
                      onChange={(e) => updateOperatingHours(index, 'day', e.target.value)}
                      label="Day"
                    >
                      {DAYS_OF_WEEK.map((day) => (
                        <MenuItem key={day} value={day}>
                          {day}
                        </MenuItem>
                      ))}
                    </Select>
                    {operatingHoursErrors[index]?.day && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                        {operatingHoursErrors[index].day}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
                
                <Grid size={{ xs: 12, sm: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={hours.isClosed}
                        onChange={(e) => updateOperatingHours(index, 'isClosed', e.target.checked)}
                      />
                    }
                    label="Closed"
                  />
                </Grid>
                
                {!hours.isClosed && (
                  <>
                    <Grid size={{ xs: 12, sm: 3 }}>
                      <TextField
                        fullWidth
                        type="time"
                        label="Open Time"
                        value={hours.openTime}
                        onChange={(e) => updateOperatingHours(index, 'openTime', e.target.value)}
                        error={!!operatingHoursErrors[index]?.openTime}
                        helperText={operatingHoursErrors[index]?.openTime}
                        slotProps={{
                          inputLabel: { shrink: true }
                        }}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 3 }}>
                      <TextField
                        fullWidth
                        type="time"
                        label="Close Time"
                        value={hours.closeTime}
                        onChange={(e) => updateOperatingHours(index, 'closeTime', e.target.value)}
                        error={!!operatingHoursErrors[index]?.closeTime}
                        helperText={operatingHoursErrors[index]?.closeTime}
                        slotProps={{
                          inputLabel: { shrink: true }
                        }}
                      />
                    </Grid>
                  </>
                )}
                
                <Grid size={{ xs: 12, sm: 1 }}>
                  <IconButton
                    onClick={() => removeOperatingHours(index)}
                    aria-label="Remove operating hours"
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </Card>
          ))}
          
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <IconButton
              onClick={addOperatingHours}
              disabled={availableDays.length === 0}
              color="primary"
              sx={{ 
                border: '2px dashed',
                borderColor: 'primary.main',
                borderRadius: 2,
                p: 2,
                '&:hover': {
                  backgroundColor: 'primary.light',
                  color: 'primary.contrastText'
                }
              }}
            >
              <AddIcon sx={{ mr: 1 }} />
              <Typography variant="button">Add Operating Hours</Typography>
            </IconButton>
          </Box>
        </Box>
      </FormSection>

      {/* Booking Configuration Section */}
      <FormSection 
        title="Booking Configuration" 
        description="Configure booking rules and constraints"
        variant="card"
        collapsible
        defaultExpanded={true}
      >
        <Grid container spacing={3} sx={{ width: '100%' }}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              type="number"
              label="Slot Duration (minutes)"
              value={formData.bookingConfig.slotDuration}
              onChange={(e) => handleNestedFieldChange('bookingConfig', 'slotDuration', parseInt(e.target.value))}
              placeholder="60"
              error={!!bookingConfigErrors.slotDuration}
              helperText={bookingConfigErrors.slotDuration || "Duration of each booking slot (15-480 minutes)"}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              type="number"
              label="Lead Time (hours)"
              value={formData.bookingConfig.leadTime}
              onChange={(e) => handleNestedFieldChange('bookingConfig', 'leadTime', parseInt(e.target.value))}
              placeholder="1"
              error={!!bookingConfigErrors.leadTime}
              helperText={bookingConfigErrors.leadTime || "Minimum hours in advance for booking (0-168 hours)"}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              type="number"
              label="Max Booking Duration (minutes)"
              value={formData.bookingConfig.maxBookingDuration}
              onChange={(e) => handleNestedFieldChange('bookingConfig', 'maxBookingDuration', parseInt(e.target.value))}
              placeholder="120"
              error={!!bookingConfigErrors.maxBookingDuration}
              helperText={bookingConfigErrors.maxBookingDuration || "Maximum duration for a single booking (15-480 minutes)"}
            />
          </Grid>
        </Grid>
      </FormSection>

      {/* Photos Section */}
      {facility?._id && (
        <FormSection 
          title="Photos" 
          description="Upload and manage photos for this facility"
          variant="card"
          collapsible
          defaultExpanded={true}
        >
          <PhotoUploadSection
            photos={photos}
            coverPhotoId={coverPhotoId}
            onUpload={handlePhotoUpload}
            onDelete={handlePhotoDelete}
            onSetCover={handleSetCoverPhoto}
            title="Facility Photos"
            description="Upload photos to showcase this facility"
            maxFiles={20}
          />
        </FormSection>
      )}

      {/* Form Actions */}
      <FormActions
        primaryAction={{
          label: facility ? 'Update Facility' : 'Create Facility',
          loading: isSubmitting,
          disabled: isSubmitting
        }}
        secondaryAction={{
          label: 'Cancel',
          onClick: onCancel,
          disabled: isSubmitting
        }}
        alignment="right"
        variant="sticky"
      />
    </Box>
  );
};

export default FacilityForm;